// hooks/useURLSearchParams.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Хук для синхронизации фильтров с URL-параметрами.
 * Поддерживает multi/single фильтры, метки и значения, даты, boolean и значения 'Все'.
 */
export const useURLSearchParams = (filterConfig) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isInitialized, setIsInitialized] = useState(false);

  // --- 🔧 Преобразование объекта фильтров в URL-параметры ---
  const objectToURLParams = useCallback((params) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return; // пропускаем пустые
      }

      // Множественные фильтры (multi)
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item && item.value) {
            searchParams.append(`${key}_label`, item.label);
            searchParams.append(`${key}_value`, item.value);
          }
        });
      }
      // Булевы значения (просто ключ=значение)
      else if (typeof value === 'boolean') {
        searchParams.append(key, value.toString());
      }
      // Объектные значения (single selector)
      else if (typeof value === 'object' && value.value) {
        searchParams.append(`${key}_label`, value.label);
        searchParams.append(`${key}_value`, value.value);
      }
      // Примитивы (строки, даты и т.д.)
      else if (typeof value === 'string' && value.trim() !== '') {
        searchParams.append(key, value);
      }
      // Числовые значения (например, timestamp для дат)
      else if (typeof value === 'number') {
        searchParams.append(key, value.toString());
      }
    });

    return searchParams.toString();
  }, []);

  // --- 🔧 Преобразование URL-параметров в объект фильтров ---
  const urlParamsToObject = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const result = {};

    filterConfig.forEach(({ key, type = 'single' }) => {
      if (type === 'multi') {
        const labels = searchParams.getAll(`${key}_label`);
        const values = searchParams.getAll(`${key}_value`);

        if (labels.length > 0 && values.length > 0) {
          result[key] = labels.map((label, i) => ({
            label: label,
            value: values[i],
          }));
        }
      } else if (type === 'date') {
        // Для дат - простое значение (timestamp)
        const dateValue = searchParams.get(key);
        if (dateValue && !isNaN(Number(dateValue))) {
          result[key] = Number(dateValue);
        } else if (dateValue) {
          result[key] = dateValue;
        }
      } else if (type === 'boolean') {
        // Для булевых значений - просто true/false
        const boolValue = searchParams.get(key);
        if (boolValue !== null) {
          result[key] = boolValue === 'true';
        }
      } else {
        // single
        const label = searchParams.get(`${key}_label`);
        const value = searchParams.get(`${key}_value`);

        if (label && value) {
          result[key] = {
            label: label,
            value,
          };
        } else {
          const singleValue = searchParams.get(key);
          if (singleValue) {
            result[key] = singleValue;
          }
        }
      }
    });

    return result;
  }, [location.search, filterConfig]);

  // --- 🧠 Мемоизация текущих параметров ---
  const currentParams = useMemo(() => {
    if (location.search) {
      const parsed = urlParamsToObject();
      return parsed;
    }
    return {};
  }, [location.search, urlParamsToObject]);

  // --- 🚀 Обновление URL ---
  const updateURL = useCallback(
    (newParams) => {
      const mergedParams = { ...currentParams };

      // Обновляем или удаляем параметры
      Object.entries(newParams).forEach(([key, value]) => {
        if (
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete mergedParams[key];
        } else {
          mergedParams[key] = value;
        }
      });

      const queryString = objectToURLParams(mergedParams);
      const newURL = queryString
        ? `${location.pathname}?${queryString}`
        : location.pathname;

      navigate(newURL, { replace: true });
    },
    [currentParams, location.pathname, navigate, objectToURLParams]
  );

  // --- 🧩 Инициализация ---
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // --- Возврат значений ---
  return {
    urlParams: currentParams,
    updateURL,
    isInitialized,
    hasInitialParams: isInitialized && Object.keys(currentParams).length > 0,
  };
};